import User from "../models/User";
import { ApiException, ILoginResponse, LoginClient, LoginRequest, CreateUserRequest } from "./WebApiClient";

export interface ILoginService {
    readonly http: IHttpService;
    readonly currentUser?: Readonly<User>;
    login(username: string, password: string): Promise<boolean>;
    logout(): void;
    createCustomer(
        username: string,
        password: string,
        name: string,
        phoneNumber: string,
        email:string
    ): Promise<boolean>;
}

export class LoginService implements ILoginService {
    private static readonly userTokenKey = "userToken";

    private loginClient: LoginClient;
    private storage: Storage;

    constructor(
        private readonly onUserChange: (u: User | undefined) => void,
    ) {
        this.storage = window.sessionStorage;
        this.http = new AuthorizationHttpService();
        this.handleLoginResponseChanged(false);
        this.loginClient = new LoginClient(undefined, this.http);
    }
    
    private setLoginResponse(lr: ILoginResponse|undefined) {
        if (lr){
            this.storage.setItem(LoginService.userTokenKey, JSON.stringify(lr));
        }
        else{
            this.storage.removeItem(LoginService.userTokenKey);
        }
        this.handleLoginResponseChanged(true);
    }

    private handleLoginResponseChanged(doCallback: boolean) {
        const lr = this.loadLoginResponse();
        let user;
        if (lr) {
            this.http.token = lr.token;
            user = new User(
                lr.userId,
                lr.username,
                lr.name,
                lr.userRights);
        }
        else {
            this.http.token = undefined;
            user = undefined;
        }
        if (doCallback) {
            this.onUserChange(user);
        }
    }

    readonly http: AuthorizationHttpService;
    currentUser?: User;

    async login(username: string, password: string): Promise<boolean> {
        try {
            const response = await this.loginClient.login(new LoginRequest({ username, password }));
            this.setLoginResponse(response);
            return true;
        } catch (ex) {
            this.setLoginResponse(undefined);
            if (ex instanceof ApiException) {
                if (ex.status === 401 || ex.status === 403) {
                    //wrong username of password
                    return false;
                }
            }
            throw ex;
        }
    }

    logout() {
        this.setLoginResponse(undefined);
    }

    private loadLoginResponse(): ILoginResponse | undefined {
        const storage = this.storage;
        const tokenJson = storage.getItem(LoginService.userTokenKey);
        if (!tokenJson)
            return undefined;
        return JSON.parse(tokenJson);
    }

    async createCustomer(
        username: string,
        password: string,
        name: string,
        phoneNumber: string,
        email: string
    ): Promise<boolean> {
        const request = new CreateUserRequest({
            username,
            password,
            email,
            name,
            phoneNumber
        });

        const response = await this.loginClient.createCustomer(request);
        this.setLoginResponse(response.login);
        return true;
    }
}

export interface IHttpService {
    fetch(url: RequestInfo, init?: RequestInit): Promise<Response>
}
export class AuthorizationHttpService implements IHttpService {

    constructor(private readonly parent: IHttpService = window) {

    }

    token?: string;

    async fetch(url: RequestInfo, init: RequestInit = {}): Promise<Response> {
        init = { ...init };
        const headers = new Headers(init.headers);
        if (this.token) {

            headers.set("Authorization", `Bearer ${this.token}`);
        }
        else {
            headers.delete("Authorization");
        }
        init.headers = headers;
        return await this.parent.fetch(url, init);
    }

}
